import { ArrayElement } from 'core/actions/Action'
import { volumesSelector, nonPaginatedVolumesSelector } from '../selectors'

export type VolumeSelectorModel =
  | ArrayElement<ReturnType<typeof volumesSelector>>
  | ArrayElement<ReturnType<typeof nonPaginatedVolumesSelector>>

/**
 * Determines if a volume can be attached based on its multiattach capability and status.
 *
 * - Regular volumes: Can only attach when status is 'available'
 * - Multiattach volumes: Can attach when status is 'available' or 'in-use' (can accept additional attachments)
 *
 * @param volume - The volume object to check
 * @returns boolean - Whether the volume can be attached
 */
export const canAttachVolume = (volume: VolumeSelectorModel) => {
  if (volume?.multiattach) {
    return ['available', 'in-use'].includes(volume?.status)
  }
  return volume?.status === 'available'
}

/**
 * Badge variant type for volume status display
 */
export type BadgeVariant = 'warning' | 'success' | 'primary' | 'error' | 'unknown'

/**
 * Maps volume status to badge variant for visual display.
 *
 * Status to variant mappings:
 * - 'warning': creating, attaching, detaching, deleting, awaiting-transfer, backing-up,
 *              restoring-backup, downloading, uploading, retyping, extending
 * - 'success': available, in-use
 * - 'primary': reserved, maintenance
 * - 'error': error, error_deleting, error_backing-up, error_restoring, error_extending
 * - 'unknown': any other status
 *
 * @param status - The volume status string
 * @returns The badge variant for the status
 */
export const getBadgeVariant = (status: string): BadgeVariant => {
  return ({
    creating: 'warning',
    available: 'success',
    reserved: 'primary',
    attaching: 'warning',
    detaching: 'warning',
    'in-use': 'success',
    maintenance: 'primary',
    deleting: 'warning',
    'awaiting-transfer': 'warning',
    error: 'error',
    error_deleting: 'error',
    'backing-up': 'warning',
    'restoring-backup': 'warning',
    'error_backing-up': 'error',
    error_restoring: 'error',
    error_extending: 'error',
    downloading: 'warning',
    uploading: 'warning',
    retyping: 'warning',
    extending: 'warning',
  }[status] || 'unknown') as BadgeVariant
}

/**
 * Maps volume status to human-readable display text.
 *
 * Converts status codes to user-friendly text:
 * - 'creating' → 'Creating'
 * - 'in-use' → 'In-Use'
 * - 'awaiting-transfer' → 'Awaiting Transfer'
 * - etc.
 *
 * @param status - The volume status string
 * @returns The human-readable text for the status
 */
export const getBadgeText = (status: string): string => {
  const text =
    {
      creating: 'Creating',
      available: 'Available',
      reserved: 'Reserved',
      attaching: 'Attaching',
      detaching: 'Detaching',
      'in-use': 'In-Use',
      maintenance: 'Maintenance',
      deleting: 'Deleting',
      'awaiting-transfer': 'Awaiting Transfer',
      error: 'Error',
      error_deleting: 'Error Deleting',
      'backing-up': 'Backing Up',
      'restoring-backup': 'Restoring Backup',
      'error_backing-up': 'Error Backing Up',
      error_restoring: 'Error Restoring',
      error_extending: 'Error Extending',
      downloading: 'Downloading',
      uploading: 'Uploading',
      retyping: 'Retyping',
      extending: 'Extending',
    }[status] || status
  return text
}
